import { Fragment } from "core/models";
import createNewFormat from "utils/createNewFormat";
import { createEdit, EditRequest, getFragment } from "core/services";
import { NotificationType } from "ui/context/NotificationsContext";
import downloadFileUrl from "utils/downloadFileUrl";

export const handleDownload = async (
    selectedOutput: string,
    fragment: Fragment,
    sessionToken: string,
    addNotification: (
        type: "normal" | "error" | "success",
        title: React.ReactNode,
        decription?: string,
        duration?: number,
    ) => NotificationType,
    updateNotification: (
        notificationID: string,
        type: "normal" | "error" | "success",
        title: React.ReactNode,
        description?: string,
        duration?: number,
    ) => void,
): Promise<void> => {
    const downloadNotification = addNotification(
        "normal",
        `Preparing ${selectedOutput}`,
        `For ${fragment.title}`,
        0,
    );
    // Check if file format exists on the fragment
    if (
        !fragment.variants.some(
            (variant) => variant.formatKey === selectedOutput,
        )
    ) {
        updateNotification(
            downloadNotification.id,
            "normal",
            `Creating ${selectedOutput}`,
            `For ${fragment.title}`,
            0,
        );
        try {
            await createNewFormat(selectedOutput, sessionToken, fragment.id);
            const updatedFragment = await getFragment(
                sessionToken,
                fragment.id,
            );
            if (updatedFragment.error) {
                throw new Error("getFragment call failed");
            } else if (updatedFragment.value) {
                const variantIndex = updatedFragment.value.variants.findIndex(
                    (variant) => variant.formatKey === selectedOutput,
                );
                if (variantIndex !== -1) {
                    updateNotification(
                        downloadNotification.id,
                        "success",
                        `${selectedOutput} created, download starting`,
                        `For ${fragment.title}`,
                        5000,
                    );
                    downloadFileUrl(
                        updatedFragment.value.getDownloadURL(variantIndex),
                    );
                } else {
                    throw new Error("Format still not found in variant");
                }
            }
        } catch (err) {
            updateNotification(
                downloadNotification.id,
                "error",
                "Something went wrong",
                `while creating ${selectedOutput} for ${fragment.title}`,
                0,
            );
        }
    } else {
        updateNotification(
            downloadNotification.id,
            "success",
            "Starting download",
            `for ${fragment.title}`,
            5000,
        );
        const variantIndex = fragment.variants.findIndex(
            (variant) => variant.formatKey === selectedOutput,
        );
        downloadFileUrl(fragment.getDownloadURL(variantIndex), fragment.title);
    }
};

export const handleEdit = async (
    editRequest: EditRequest,
    fragment: Fragment,
    sessionToken: string,
    addNotification: (
        type: "normal" | "error" | "success",
        title: React.ReactNode,
        decription?: string,
        duration?: number,
    ) => NotificationType,
    updateNotification: (
        notificationID: string,
        type: "normal" | "error" | "success",
        title: React.ReactNode,
        description?: string,
        duration?: number,
    ) => void,
    onEdit: (
        fragment: Fragment,
        notificationId: string,
        successText: string,
    ) => void,
): Promise<void> => {
    const editNotification = addNotification(
        "normal",
        `Edit in progress`,
        `For ${fragment.title}`,
        0,
    );
    try {
        const editedFragment = await createEdit(sessionToken, editRequest);
        if (editedFragment.error) {
            throw new Error("Failed creating Edit");
        } else if (
            editedFragment.value &&
            editedFragment.value.processingTaskId !== 0
        ) {
            onEdit(
                editedFragment.value,
                editNotification.id,
                `Edit for ${fragment.title} is ready`,
            );
        } else {
            throw new Error("no processingTaskId found on fragment");
        }
    } catch (error) {
        updateNotification(
            editNotification.id,
            "error",
            "Error on Edit clip",
            `Error: ${error}`,
            0,
        );
    }
};
